import React, { useState, useCallback, useMemo } from 'react';
import DropZone from '../components/DropZone';
import RequestTable from '../components/RequestTable';
import DetailPanel from '../components/DetailPanel';
import ExportModal from '../components/ExportModal';
import { parseHAR, fmtSize, fmtTime, isInScope, copyToClipboard } from '../utils/harUtils';
import styles from './ViewerPage.module.css';

const TYPE_FILTERS = ['all', 'xhr', 'js', 'css', 'img', 'media', 'font', 'doc', 'other', 'errors'];
const TYPE_LABELS  = { all:'All', xhr:'XHR / Fetch', js:'JS', css:'CSS', img:'Img', media:'Media', font:'Font', doc:'Doc', other:'Other', errors:'Errors' };

export default function ViewerPage({ onStats, onToast }) {
  const [allRequests, setAllRequests]   = useState([]);
  const [fileName, setFileName]         = useState('');
  const [search, setSearch]             = useState('');
  const [target, setTarget]             = useState('');
  const [typeFilter, setTypeFilter]     = useState('all');
  const [scopeFilter, setScopeFilter]   = useState('all');
  const [selectedReq, setSelectedReq]   = useState(null);
  const [showExport, setShowExport]     = useState(false);

  const handleLoad = useCallback((data, name) => {
    const parsed = parseHAR(data);
    setAllRequests(parsed);
    setFileName(name);
    setSearch('');
    setTypeFilter('all');
    setScopeFilter('all');
    setSelectedReq(null);

    const totalSize = parsed.reduce((s, r) => s + (r.size || 0), 0);
    const maxTime   = parsed.length ? Math.max(...parsed.map(r => r.time || 0)) : 0;
    const errors    = parsed.filter(r => r.status >= 400 || r.status === 0).length;
    onStats({ total: parsed.length, size: fmtSize(totalSize), time: fmtTime(maxTime), errors });
  }, [onStats]);

  const filteredRequests = useMemo(() => {
    const searchLower = search.toLowerCase();
    return allRequests.filter(req => {
      // Type
      if (typeFilter === 'errors') {
        if (req.status < 400 && req.status !== 0) return false;
      } else if (typeFilter !== 'all') {
        if (req.typeCategory !== typeFilter) return false;
      }
      // Scope
      if (scopeFilter === 'inscope' && !isInScope(req.domain, target)) return false;
      if (scopeFilter === 'external' && (!target || isInScope(req.domain, target))) return false;
      // Search
      if (searchLower) {
        const haystack = [
          req.url,
          ...req.reqHeaders.map(h => h.name + ':' + h.value),
          ...req.queryParams.map(p => p.name + '=' + p.value),
        ].join(' ').toLowerCase();
        if (!haystack.includes(searchLower)) return false;
      }
      return true;
    });
  }, [allRequests, typeFilter, scopeFilter, target, search]);

  // Scope counts (over type+search filtered pool)
  const basePool = useMemo(() => {
    const searchLower = search.toLowerCase();
    return allRequests.filter(req => {
      if (typeFilter === 'errors') { if (req.status < 400 && req.status !== 0) return false; }
      else if (typeFilter !== 'all') { if (req.typeCategory !== typeFilter) return false; }
      if (searchLower) {
        const h = [req.url, ...req.reqHeaders.map(h=>h.name+':'+h.value), ...req.queryParams.map(p=>p.name+'='+p.value)].join(' ').toLowerCase();
        if (!h.includes(searchLower)) return false;
      }
      return true;
    });
  }, [allRequests, typeFilter, search]);

  const inScopeCount  = useMemo(() => target ? basePool.filter(r => isInScope(r.domain, target)).length : null, [basePool, target]);
  const externalCount = useMemo(() => target ? basePool.filter(r => !isInScope(r.domain, target)).length : null, [basePool, target]);

  const handleSelectReq = useCallback((req) => {
    setSelectedReq(req);
    copyToClipboard(req.url).then(() => onToast('✓ URL Copied!'));
  }, [onToast]);

  const handleScopeFilter = (f) => {
    if ((f === 'inscope' || f === 'external') && !target) {
      document.getElementById('target-input').focus();
      document.getElementById('target-input').classList.add(styles.inputShake);
      setTimeout(() => document.getElementById('target-input').classList.remove(styles.inputShake), 600);
      return;
    }
    setScopeFilter(f);
  };

  const clearAll = () => {
    setAllRequests([]);
    setFileName('');
    setSearch('');
    setTarget('');
    setTypeFilter('all');
    setScopeFilter('all');
    setSelectedReq(null);
    onStats(null);
  };

  if (allRequests.length === 0) {
    return <DropZone onLoad={handleLoad} />;
  }

  return (
    <div className={styles.viewer}>
      {/* TOOLBAR */}
      <div className={styles.toolbar}>
        <div className={styles.searchWrap}>
          <svg className={styles.searchIcon} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
          </svg>
          <input
            className={styles.searchInput}
            placeholder="Search URLs, headers, params..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {search && (
            <button className={styles.clearSearch} onClick={() => setSearch('')}>✕</button>
          )}
        </div>

        <span className={styles.scopeLabel}>🎯 Target:</span>
        <input
          id="target-input"
          className={styles.targetInput}
          placeholder="example.com"
          value={target}
          onChange={e => setTarget(e.target.value)}
        />

        <button className={`${styles.btn} ${styles.btnExport}`} onClick={() => setShowExport(true)}>
          ⬇ Export URLs
        </button>
        <button className={`${styles.btn} ${styles.btnClear}`} onClick={clearAll}>
          ✕ Clear
        </button>
      </div>

      {/* TYPE FILTER BAR */}
      <div className={styles.typeBar}>
        {TYPE_FILTERS.map(t => (
          <button
            key={t}
            className={`${styles.typeBtn} ${typeFilter === t ? styles.typeBtnActive : ''} ${t === 'errors' ? styles.typeBtnErrors : ''}`}
            onClick={() => setTypeFilter(t)}
          >
            {TYPE_LABELS[t]}
          </button>
        ))}
      </div>

      {/* SCOPE BAR */}
      <div className={styles.scopeBar}>
        <span className={styles.scopeBarLabel}>Scope:</span>
        <button
          className={`${styles.scopeChip} ${scopeFilter === 'all' ? styles.scopeChipAll : ''}`}
          onClick={() => setScopeFilter('all')}
        >
          All <span className={styles.cnt}>{basePool.length}</span>
        </button>
        <button
          className={`${styles.scopeChip} ${scopeFilter === 'inscope' ? styles.scopeChipInscope : ''}`}
          onClick={() => handleScopeFilter('inscope')}
        >
          🎯 In-Scope <span className={styles.cnt}>{inScopeCount ?? '?'}</span>
        </button>
        <button
          className={`${styles.scopeChip} ${scopeFilter === 'external' ? styles.scopeChipExternal : ''}`}
          onClick={() => handleScopeFilter('external')}
        >
          🌐 External <span className={styles.cnt}>{externalCount ?? '?'}</span>
        </button>
        {!target && (
          <span className={styles.scopeHint}>← Enter a target domain to enable scope filters</span>
        )}
      </div>

      {/* RESULTS BAR */}
      <div className={styles.resultsBar}>
        <span>
          Showing <strong className={styles.accentNum}>{filteredRequests.length}</strong> of{' '}
          <strong className={styles.accentNum}>{allRequests.length}</strong> requests
        </span>
        <span className={styles.fileName}>📄 {fileName}</span>
      </div>

      {/* TABLE */}
      <RequestTable
        requests={filteredRequests}
        target={target}
        onSelect={handleSelectReq}
      />

      {/* DETAIL PANEL */}
      {selectedReq && (
        <DetailPanel
          request={selectedReq}
          onClose={() => setSelectedReq(null)}
          onToast={onToast}
        />
      )}

      {/* EXPORT MODAL */}
      {showExport && (
        <ExportModal
          requests={filteredRequests}
          activeType={typeFilter}
          activeScope={scopeFilter}
          searchTerm={search}
          fileName={fileName}
          onClose={() => setShowExport(false)}
          onToast={onToast}
        />
      )}
    </div>
  );
}
